import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
    AtokuTermsAndConditionsDocument,
    AtokuTermsAndConditionsUpdateRequest
} from '../../../services/atoku.service';

export interface TermsAndConditionsDialogData {
    document: AtokuTermsAndConditionsDocument;
}

@Component({
    selector: 'app-terms-and-conditions-dialog',
    templateUrl: './terms-and-conditions-dialog.component.html',
    styleUrls: ['./terms-and-conditions-dialog.component.scss']
})
export class TermsAndConditionsDialogComponent {
    readonly form: FormGroup;

    constructor(
        private readonly fb: FormBuilder,
        private readonly dialogRef: MatDialogRef<TermsAndConditionsDialogComponent, AtokuTermsAndConditionsUpdateRequest>,
        private readonly sanitizer: DomSanitizer,
        @Inject(MAT_DIALOG_DATA) public readonly data: TermsAndConditionsDialogData
    ) {
        this.form = this.fb.group({
            name: [data.document.name || 'Terms & Conditions'],
            content: [data.document.content || this.defaultMarkdown()]
        });
    }

    get contentLength(): number {
        return String(this.form.get('content')?.value ?? '').length;
    }

    get documentStatus(): string {
        if (!this.data.document.exists) {
            return 'New document';
        }

        return `Version ${this.data.document.version || 1}`;
    }

    get markdownPreviewHtml(): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(
            this.renderMarkdown(String(this.form.get('content')?.value ?? ''))
        );
    }

    cancel(): void {
        this.dialogRef.close();
    }

    save(): void {
        const values = this.form.getRawValue();
        this.dialogRef.close({
            name: String(values.name ?? '').trim() || 'Terms & Conditions',
            content: String(values.content ?? '')
        });
    }

    private defaultMarkdown(): string {
        return `# Terms and Conditions

## 1. Introduction

Add the booking terms that apply to this company.

## 2. Cancellations

1. Add the first condition.
2. Add the second condition.
`;
    }

    private renderMarkdown(markdown: string): string {
        const escaped = this.escapeHtml(markdown);
        const lines = escaped.split(/\r?\n/);
        const html: string[] = [];
        let inOrderedList = false;
        let inUnorderedList = false;

        const closeLists = () => {
            if (inOrderedList) {
                html.push('</ol>');
                inOrderedList = false;
            }
            if (inUnorderedList) {
                html.push('</ul>');
                inUnorderedList = false;
            }
        };

        for (const rawLine of lines) {
            const line = rawLine.trim();
            if (!line) {
                closeLists();
                continue;
            }

            const heading = /^(#{1,6})\s+(.+)$/.exec(line);
            if (heading) {
                closeLists();
                const level = heading[1].length;
                html.push(`<h${level}>${this.renderInlineMarkdown(heading[2])}</h${level}>`);
                continue;
            }

            const ordered = /^\d+\.\s+(.+)$/.exec(line);
            if (ordered) {
                if (inUnorderedList) {
                    html.push('</ul>');
                    inUnorderedList = false;
                }
                if (!inOrderedList) {
                    html.push('<ol>');
                    inOrderedList = true;
                }
                html.push(`<li>${this.renderInlineMarkdown(ordered[1])}</li>`);
                continue;
            }

            const unordered = /^[-*]\s+(.+)$/.exec(line);
            if (unordered) {
                if (inOrderedList) {
                    html.push('</ol>');
                    inOrderedList = false;
                }
                if (!inUnorderedList) {
                    html.push('<ul>');
                    inUnorderedList = true;
                }
                html.push(`<li>${this.renderInlineMarkdown(unordered[1])}</li>`);
                continue;
            }

            closeLists();
            html.push(`<p>${this.renderInlineMarkdown(line)}</p>`);
        }

        closeLists();
        return html.join('');
    }

    private renderInlineMarkdown(value: string): string {
        return value
            .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
            .replace(/\*(.+?)\*/g, '<em>$1</em>');
    }

    private escapeHtml(value: string): string {
        return value
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
    }
}
